import { DeliveryMode, IOrderPackage, PackageStatus } from '@admin-panel/types';
import { Tag } from 'antd';
import { PACKAGE_LABELS } from './packageLabels';

/** The own-fleet rider's "undelivered" marker on the package's current attempt. */
export const isRiderMarkedUndelivered = (pkg?: Pick<IOrderPackage, 'courierMetadata'> | null) =>
  pkg?.courierMetadata?.COURIER_STATUS?.trim().toUpperCase() === 'UNDELIVERED';

/**
 * A failed delivery keeps the package OUT_FOR_DELIVERY (or DISPATCHED for a courier) until the next
 * attempt or an RTO, so the lifecycle name alone reads as if the parcel were still on its way.
 * Every package status tag says "Undelivered" for that state.
 */
export const isUndeliveredAttempt = (
  pkg?: Pick<IOrderPackage, 'currentStatus' | 'courierMetadata' | 'courierDeliveryFailure'> | null,
) =>
  Boolean(
    pkg &&
    (pkg.currentStatus === PackageStatus.DISPATCHED ||
      pkg.currentStatus === PackageStatus.OUT_FOR_DELIVERY) &&
    (isRiderMarkedUndelivered(pkg) || pkg.courierDeliveryFailure),
  );

export const deliveryStatusLabel = (
  pkg: Pick<
    IOrderPackage,
    'currentStatus' | 'deliveryMode' | 'courierMetadata' | 'courierDeliveryFailure'
  >,
) =>
  isUndeliveredAttempt(pkg)
    ? 'Undelivered'
    : getPackageStatusDisplayName(pkg.currentStatus, pkg.deliveryMode);

/** Status first, then Try at home, in the shared package words (packageLabels.ts). */
export const OrderPackageStatusTag = (props: { orderPackage: IOrderPackage }) => {
  const undelivered = isUndeliveredAttempt(props.orderPackage);
  return (
    <>
      <Tag
        color={undelivered ? 'volcano' : 'magenta'}
        style={{ marginRight: 0, fontSize: 12, padding: '2px 8px' }}
      >
        {deliveryStatusLabel(props.orderPackage)}
      </Tag>
      {props.orderPackage?.tryAtHome && (
        <Tag
          color="red"
          style={{ marginLeft: 6, marginRight: 0, fontSize: 12, padding: '2px 8px' }}
        >
          {PACKAGE_LABELS.tryAtHome}
        </Tag>
      )}
    </>
  );
};

export const getPackageStatusDisplayName = (status: PackageStatus, deliveryMode?: DeliveryMode) => {
  switch (status) {
    case PackageStatus.CREATED:
      return 'Created';
    case PackageStatus.FULFILLABLE:
      return 'Fulfillable';
    case PackageStatus.WIP:
      return 'Work In Progress';
    case PackageStatus.PACKED:
      return 'Packed';
    case PackageStatus.DISPATCHED:
      return 'In Transit';
    case PackageStatus.DELIVERED_AT_STORE:
      return 'Arrived At Pickup Store';
    case PackageStatus.RECEIVED_AT_STORE:
      return 'Ready For Store Pickup';
    case PackageStatus.OUT_FOR_DELIVERY:
      return 'Out For Delivery';
    case PackageStatus.CANCELLED:
      return 'Cancelled';
    case PackageStatus.RTO_INITIATED:
      return 'RTO Initiated';
    case PackageStatus.RTO:
      return 'Returned To Origin';
    case PackageStatus.DELIVERED:
      if (deliveryMode === DeliveryMode.STORE_PICKUP) return 'Pickup Complete';
      else return 'Delivered';
    default:
      return status;
  }
};
